import { inject, Injectable } from '@angular/core';
import { ProductFeatureClient, ProductFeatureModel, ProductUserTypeGroupingEnum } from '@rma/billing-api';
import { AgencyProfileService, BillingUserContextService, DashboardProfileContextService } from '@rma/dashboard/authentication/public/user';
import {
  CurrentSubscriptionService,
  SubscriptionProductService,
} from '@rma/dashboard/billing/subscription-plans/public/api-subscription-features';
import { Environment } from '@rma/dashboard/generic/util/environment';
import { FeatureFlagService, TempFeatureFlags } from '@rma/dashboard/generic/util/feature-flags';
import { SyndicationStatisticsService } from '@rma/dashboard/reporting/public/api-review-syndication-statistics';
import { SyndicationChannel } from '@rma/dashboard/reporting/public/ui-syndication-channels';
import { REPORTING_TIME_PERIOD } from '@rma/dashboard/reporting/public/util-reporting-time-period';
import { ConvertedReviewInsights } from '@rma/dashboard/reviews/public/api-reviews-pangea';
import { isIndividual } from '@rma/generic/domain/helpers';
import { nonNullable } from '@rma/generic/rxjs/operators/non-nullable';
import { rmaShareReplay } from '@rma/generic/rxjs/operators/rma-share-replay';
import { catchError, combineLatest, map, Observable, of, startWith, switchMap } from 'rxjs';
import { PotentialReviewsApiService } from '../domain/potential-reviews-api.service';
import { SearchReviewsService } from '../domain/search-reviews.service';
import { PauseSubscriptionService } from '../public/pause-subscription.service';

@Injectable()
export class MySubscriptionFacadeService {
  private readonly agencyProfileService = inject(AgencyProfileService);
  private readonly billingUserContextService = inject(BillingUserContextService);
  private readonly currentSubscriptionService = inject(CurrentSubscriptionService);
  private readonly environment = inject(Environment);
  private readonly featureService = inject(FeatureFlagService);
  private readonly pauseSubscriptionService = inject(PauseSubscriptionService);
  private readonly potentialReviewsService = inject(PotentialReviewsApiService);
  private readonly productFeatureApiservice = inject(ProductFeatureClient);
  private readonly profileContextService = inject(DashboardProfileContextService);
  private readonly reviewSearchService = inject(SearchReviewsService);
  private readonly subscriptionProductService = inject(SubscriptionProductService);
  private readonly syndicationStaticticsService = inject(SyndicationStatisticsService);
  private readonly timePeriod = inject(REPORTING_TIME_PERIOD);

  public readonly imagePath = `dashboard/my-subscription/reviews-shared-instantly-${this.environment.tenant}.png`;
  public readonly externalPlatformsWithReviews$ = this.syndicationStaticticsService.externalPlatformsWithReviews$;
  public readonly externalReviewPlatform$ = this.syndicationStaticticsService.externalReviewPlatform$;
  public readonly googleReviewInsights$ = this.syndicationStaticticsService.googleReviewInsights$;
  public readonly platformIcon$ = this.syndicationStaticticsService.platformIcon$;
  public readonly realtorDomainExposures$ = this.syndicationStaticticsService.realtorDomainExposures$;
  public readonly rmaExposures$ = this.syndicationStaticticsService.rmaExposures$;
  public readonly rmaReviewCount$ = this.reviewSearchService.getProfileReviewCount(this.timePeriod.from);
  public readonly subscription$ = this.currentSubscriptionService.currentSubscription$;
  public readonly unsubscribeUrl$ = this.currentSubscriptionService.unsubscribeUrl$;
  public readonly profile$ = this.profileContextService.profileContext$;
  public readonly agentCount$ = this.getAgentCount();
  public readonly connectedGoogleAccountOwnerName$ = this.getConnectedGoogleAccountOwnerName();
  public readonly goodPerformance$ = this.getGoodPerfomance();
  public readonly potentialReviews$ = this.getPotentialReviews();
  public readonly productFeatures$ = this.subscription$.pipe(switchMap((sub) => this.getProductFeatures(sub.productId)));
  public readonly showCancel$ = this.getShowCancel();
  public readonly showBrokerageOverrideUrl$ = this.getBrokerageOverrideUrl();

  public readonly showPauseSubscription$ = this.pauseSubscriptionService.showPauseSubscription$;

  private getProductFeatures(productId: number | undefined): Observable<ProductFeatureModel[] | null> {
    return productId ? this.productFeatureApiservice.getProductFeatures(productId) : of(null);
  }

  private getGoodPerfomance() {
    return combineLatest([
      this.externalPlatformsWithReviews$.pipe(catchError(() => of(null))),
      this.googleReviewInsights$.pipe(catchError(() => of(null))),
      this.realtorDomainExposures$.pipe(
        catchError(() => of(null)),
        startWith(null),
      ),
      this.rmaReviewCount$.pipe(catchError(() => of(null))),
      this.rmaExposures$.pipe(catchError(() => of(null))),
    ]).pipe(
      map(([externalPlatformsWithReviews, googleReviewInsights, realtorDomainExposures, rmaReviewCount, rmaExposures]) =>
        this.isGoodPerformance(
          externalPlatformsWithReviews,
          googleReviewInsights,
          realtorDomainExposures?.convertedReviews ?? 0,
          rmaReviewCount,
          rmaExposures,
        ),
      ),
      rmaShareReplay(),
    );
  }

  private isGoodPerformance(
    externalPlatformsWithReviews: SyndicationChannel[] | null,
    googleReviewInsights: ConvertedReviewInsights | null,
    realtorDomainExposures: number | null,
    rmaReviewCount: number | null,
    rmaExposures: number | null,
  ): boolean {
    const numberOfExternalPlatformsIsGood = externalPlatformsWithReviews && externalPlatformsWithReviews.length > 1;
    const googleReviewInsightsIsGood =
      (googleReviewInsights?.averageRating?.end ?? 0) > (googleReviewInsights?.averageRating?.start ?? 0) ||
      (googleReviewInsights?.reviewsCount?.end ?? 0) > (googleReviewInsights?.reviewsCount?.start ?? 0);
    const realtorOrDomainExposuresAreGood = realtorDomainExposures && realtorDomainExposures >= 3;
    const rmaExposuresAreGood = rmaReviewCount && rmaExposures && rmaReviewCount >= 3 && rmaExposures >= 100;

    const thingsThatAreGood = [
      numberOfExternalPlatformsIsGood,
      googleReviewInsightsIsGood,
      realtorOrDomainExposuresAreGood,
      rmaExposuresAreGood,
    ].filter((isGood) => isGood).length;

    // More than one stat box has good results, that sounds like good performance to me.
    return thingsThatAreGood >= 2;
  }

  private getShowCancel() {
    return combineLatest([this.subscription$, this.subscriptionProductService.currentSubscriptionProducts$]).pipe(
      map(
        ([subscription, products]) =>
          subscription.isSubscribed && !subscription.hasCancelled && !subscription.isProvided && products.length === 1,
      ),
    );
  }

  private getPotentialReviews() {
    return this.profileContextService.profileIdentifier$.pipe(
      switchMap(({ profileType, profileCode }) =>
        this.potentialReviewsService.getPotentialRealterGoogleReviewCount(profileCode, profileType),
      ),
      nonNullable(),
    );
  }

  private getConnectedGoogleAccountOwnerName() {
    return combineLatest([this.profileContextService.profileIdentifier$, this.googleReviewInsights$]).pipe(
      map(([context, googleInsights]) =>
        context.profileType === googleInsights.ownersProfileType && context.profileCode === googleInsights.ownersProfileCode
          ? null
          : (googleInsights.ownersProfileName ?? null),
      ),
    );
  }

  //This is to handle the UPgrade button on the banner in /manage-account/subscription page that takes users straight to checkout.
  //In the US Offices don't have access to checkout so we will handle it with a flag for now as we are giving them access to their dashboards now with no subscriptions.
  private getBrokerageOverrideUrl(): Observable<boolean | null> {
    return combineLatest([
      this.billingUserContextService.userContext$,
      this.featureService.getFeature$(TempFeatureFlags.EnableBrokerageProInterestCheck, false),
      this.featureService.getFeature$(TempFeatureFlags.OfficeSubscriptionContactRequest, null),
    ]).pipe(
      map(([userContext, featureEnabled, contactUrl]) => {
        const isOffice = userContext.productUserGroupingType === ProductUserTypeGroupingEnum.Office;
        return isOffice && featureEnabled && Boolean(contactUrl) ? contactUrl : null;
      }),
    );
  }

  private getAgentCount(): Observable<number> {
    return this.profileContextService.profileIdentifier$.pipe(
      switchMap(({ profileType, profileCode }) => {
        if (isIndividual(profileType)) {
          return of(1);
        }
        return this.agencyProfileService.getAgencyProfileDetails(profileCode).pipe(
          map((agency) => agency?.Agents?.filter((agent) => agent.IsSubscribedViaAgency).length ?? 0),
          catchError(() => of(0)),
        );
      }),
      startWith(0),
      rmaShareReplay(),
    );
  }
}
